import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import ParallaxBanner from '../components/ParallaxBanner';
import contentService from '../services/contentService';
import featureFlags from '../utils/featureFlags';
import useContentUpdates from '../hooks/useContentUpdates';
import { orderService } from '../services/orderService';
import { formatOrderPrice } from '../utils/apiHelpers';
import { trackPurchase } from '../utils/analytics';

// Get payment status badge color
const getPaymentStatusBadgeClass = (paymentStatus) => {
  switch (paymentStatus?.toUpperCase()) {
    case 'PAID':
      return 'badge-success';
    case 'PENDING':
      return 'badge-warning';
    case 'FAILED':
    case 'CANCELLED':
    case 'REFUNDED':
      return 'badge-danger';
    default:
      return 'badge-secondary';
  }
};

function OrderConfirmation() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch order details
  useEffect(() => {
    const fetchOrder = async () => {
      if (!orderId) {
        setError('No order ID provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const response = await orderService.getOrder(orderId);

        if (response.success && response.data) {
          setOrder(response.data);
          // Track purchase event
          trackPurchase(response.data);
        } else {
          throw new Error(response.error?.message || 'Failed to load order');
        }
      } catch (err) {
        console.error('[OrderConfirmation] Error fetching order:', err);
        setError(err.message || 'Failed to load order details');
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [orderId]);

  // Initialize scripts
  useEffect(() => {
    const handleWindowLoad = () => {
      if (window.$ && window.$('.loader1').length > 0) {
        window.$(".loader1").fadeOut(800);
        window.$('.side-menu').removeClass('hidden');
      }
    };

    if (document.readyState === 'complete') {
      handleWindowLoad();
    } else {
      window.addEventListener('load', handleWindowLoad);
    }

    if (window.WOW) {
      new window.WOW().init();
    }

    return () => {
      window.removeEventListener('load', handleWindowLoad);
    };
  }, []);


  const [backgroundUrl, setBackgroundUrl] = useState(null);

  const fetchBackground = useCallback(async () => {
    if (!featureFlags.useDynamicBackgrounds()) {
      return;
    }
    try {
      const result = await contentService.getPageBackground('product-listing');
      if (result.success && result.imageUrl) {
        setBackgroundUrl(result.imageUrl);
      }
    } catch (err) {
      console.error('[OrderConfirmation] Error fetching background:', err);
    }
  }, []);

  useEffect(() => {
    fetchBackground();
  }, [fetchBackground]);

  useContentUpdates({
    onPageBackgroundUpdated: (page) => {
      if (page === 'product-listing') {
        fetchBackground();
      }
    }
  });

  return (
    <Layout>
      {/* Parallax Banner */}
      {backgroundUrl && (
        <ParallaxBanner
          imageUrl={backgroundUrl}
          speed={0.6}
          offset={0}
          className="paralax-section-slide-data"
        />
      )}

      {/* Order Confirmation Section */}
      <div className="about_content">
        <div className="container">
          {loading ? (
            <div className="row">
              <div className="col-12 text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
                <p className="mt-3">Loading order details...</p>
              </div>
            </div>
          ) : error ? (
            <div className="row">
              <div className="col-12 text-center py-5">
                <div className="alert alert-danger">
                  <i className="fas fa-exclamation-triangle mr-2"></i>
                  {error}
                </div>
                <Link to="/" className="btn pink-gradient-btn-into-black">
                  Return to Home
                </Link>
              </div>
            </div>
          ) : order ? (
            <>
              {/* Order Status Message - changes based on payment status */}
              <div className="row">
                <div className="col-12 text-center py-5 wow fadeIn" data-wow-duration="1s">
                  {/* Icon based on payment status */}
                  <div className="status-icon mb-4">
                    {order.paymentStatus?.toUpperCase() === 'PAID' ? (
                      <i className="fas fa-check-circle" style={{
                        fontSize: '80px',
                        color: '#28a745'
                      }}></i>
                    ) : order.paymentStatus?.toUpperCase() === 'FAILED' ? (
                      <i className="fas fa-times-circle" style={{
                        fontSize: '80px',
                        color: '#dc3545'
                      }}></i>
                    ) : (
                      <i className="fas fa-clock" style={{
                        fontSize: '80px',
                        color: '#ffc107'
                      }}></i>
                    )}
                  </div>

                  {/* Title based on payment status */}
                  <h1 className="heading mb-3">
                    {order.paymentStatus?.toUpperCase() === 'PAID'
                      ? 'Order Confirmed!'
                      : order.paymentStatus?.toUpperCase() === 'FAILED'
                      ? 'Payment Failed'
                      : 'Payment Pending'}
                  </h1>

                  {/* Message based on payment status */}
                  <p className="para_text mb-4">
                    {order.paymentStatus?.toUpperCase() === 'PAID'
                      ? 'Thank you for your purchase. Your order has been successfully placed.'
                      : order.paymentStatus?.toUpperCase() === 'FAILED'
                      ? 'Your payment could not be processed. Please try again or use a different payment method.'
                      : 'Your order has been created and is awaiting payment confirmation.'}
                  </p>

                  <div className="order-number-display mb-4">
                    <h3 style={{
                      display: 'inline-block',
                      padding: '15px 30px',
                      background: '#f8f9fa',
                      borderRadius: '8px',
                      border: `2px solid ${
                        order.paymentStatus?.toUpperCase() === 'PAID' ? '#28a745' :
                        order.paymentStatus?.toUpperCase() === 'FAILED' ? '#dc3545' : '#ffc107'
                      }`
                    }}>
                      Order Number: <strong style={{
                        color: order.paymentStatus?.toUpperCase() === 'PAID' ? '#28a745' :
                               order.paymentStatus?.toUpperCase() === 'FAILED' ? '#dc3545' : '#ffc107'
                      }}>{order.orderNumber}</strong>
                    </h3>
                  </div>
                  <p className="text-muted">
                    {order.paymentStatus?.toUpperCase() === 'PAID'
                      ? <>A confirmation email has been sent to <strong>{order.email}</strong></>
                      : order.paymentStatus?.toUpperCase() === 'FAILED'
                      ? <>Please contact support if you need assistance.</>
                      : <>You will receive a confirmation email once payment is complete.</>}
                  </p>
                </div>
              </div>

              {/* Order Details */}
              <div className="row mt-4">
                <div className="col-lg-8 mx-auto">
                  {/* Order Info */}
                  <div className="card mb-4 wow slideInUp" data-wow-duration="1s">
                    <div className="card-body">
                      <h4 className="card-title mb-4">Order Information</h4>
                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <p className="mb-1"><strong>Order Date:</strong></p>
                          <p>{new Date(order.createdAt).toLocaleDateString('en-GB', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}</p>
                        </div>
                        <div className="col-md-6 mb-3">
                          <p className="mb-1"><strong>Payment Status:</strong></p>
                          <p>
                            <span className={`badge ${getPaymentStatusBadgeClass(order.paymentStatus)}`} style={{
                              padding: '8px 12px',
                              fontSize: '14px',
                              textTransform: 'uppercase'
                            }}>
                              {order.paymentStatus || 'PENDING'}
                            </span>
                          </p>
                        </div>
                        <div className="col-md-6 mb-3">
                          <p className="mb-1"><strong>Payment Method:</strong></p>
                          <p style={{ textTransform: 'capitalize' }}>
                            {order.paymentMethod === 'stripe_card' ? 'Credit/Debit Card' : order.paymentMethod}
                          </p>
                        </div>
                        <div className="col-md-6 mb-3">
                          <p className="mb-1"><strong>Email:</strong></p>
                          <p>{order.email}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Shipping Address */}
                  {order.shippingAddress && (
                    <div className="card mb-4 wow slideInUp" data-wow-duration="1.2s">
                      <div className="card-body">
                        <h4 className="card-title mb-3">Shipping Address</h4>
                        <p className="mb-0">
                          {order.shippingAddress.fullName ||
                           `${order.shippingAddress.firstName} ${order.shippingAddress.lastName}`}<br />
                          {order.shippingAddress.address1}<br />
                          {order.shippingAddress.address2 && <>{order.shippingAddress.address2}<br /></>}
                          {order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.zipCode}<br />
                          {order.shippingAddress.country}<br />
                          {order.shippingAddress.phone && <>Phone: {order.shippingAddress.phone}</>}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Order Items */}
                  <div className="card mb-4 wow slideInUp" data-wow-duration="1.4s">
                    <div className="card-body">
                      <h4 className="card-title mb-4">Order Items</h4>
                      <div className="table-responsive">
                        <table className="table">
                          <thead>
                            <tr>
                              <th>Product</th>
                              <th className="text-center">Quantity</th>
                              <th className="text-right">Price</th>
                              <th className="text-right">Subtotal</th>
                            </tr>
                          </thead>
                          <tbody>
                            {order.items?.map((item, index) => (
                              <tr key={item.id || index}>
                                <td>
                                  <div className="d-flex align-items-center">
                                    {item.product?.images?.[0]?.url && (
                                      <img
                                        src={item.product.images[0].url}
                                        alt={item.productName || item.product?.name}
                                        style={{
                                          width: '60px',
                                          height: '60px',
                                          objectFit: 'cover',
                                          marginRight: '15px',
                                          borderRadius: '4px'
                                        }}
                                      />
                                    )}
                                    <div>
                                      <h6 className="mb-0">{item.productName || item.product?.name}</h6>
                                      {item.personalization && (
                                        <small className="text-muted">
                                          Personalized: {item.personalization}
                                        </small>
                                      )}
                                    </div>
                                  </div>
                                </td>
                                <td className="text-center align-middle">
                                  {item.quantity}
                                </td>
                                <td className="text-right align-middle">
                                  {formatOrderPrice(item.price)}
                                </td>
                                <td className="text-right align-middle">
                                  <strong>{formatOrderPrice(item.price * item.quantity)}</strong>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  {/* Order Summary */}
                  <div className="card mb-4 wow slideInUp" data-wow-duration="1.6s">
                    <div className="card-body">
                      <h4 className="card-title mb-4">Order Summary</h4>
                      <div className="d-flex justify-content-between mb-2">
                        <span>Subtotal:</span>
                        <span>{formatOrderPrice(order.summary?.subtotal || order.subtotal || 0)}</span>
                      </div>
                      {order.summary?.shipping !== undefined && (
                        <div className="d-flex justify-content-between mb-2">
                          <span>Shipping:</span>
                          <span>
                            {order.summary.shipping === 0 ? 'FREE' : formatOrderPrice(order.summary.shipping)}
                          </span>
                        </div>
                      )}
                      {order.summary?.tax !== undefined && order.summary.tax > 0 && (
                        <div className="d-flex justify-content-between mb-2">
                          <span>Tax:</span>
                          <span>{formatOrderPrice(order.summary.tax)}</span>
                        </div>
                      )}
                      {order.summary?.discount !== undefined && order.summary.discount > 0 && (
                        <div className="d-flex justify-content-between mb-2 text-success">
                          <span>Discount:</span>
                          <span>-{formatOrderPrice(order.summary.discount)}</span>
                        </div>
                      )}
                      <hr />
                      <div className="d-flex justify-content-between">
                        <strong style={{ fontSize: '18px' }}>Total:</strong>
                        <strong style={{ fontSize: '18px', color: '#007bff' }}>
                          {formatOrderPrice(order.summary?.total || order.total || 0)}
                        </strong>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="row mb-5">
                    <div className="col-12 text-center wow fadeIn" data-wow-duration="1.8s">
                      <Link to="/" className="btn pink-gradient-btn-into-black mr-3 mb-2">
                        <i className="fas fa-shopping-bag mr-2"></i>
                        Continue Shopping
                      </Link>
                      {/* TODO: Enable Track Order button when order tracking feature is implemented
                      {order.id && (
                        <Link to={`/orders/${order.id}`} className="btn btn-outline-primary mb-2">
                          <i className="fas fa-truck mr-2"></i>
                          Track Order
                        </Link>
                      )}
                      */}
                    </div>
                  </div>

                  {/* TODO: Enable "What's Next?" section when email confirmation and tracking features are implemented
                  <div className="alert alert-info wow fadeIn" data-wow-duration="2s">
                    <h5 className="alert-heading">
                      <i className="fas fa-info-circle mr-2"></i>
                      What's Next?
                    </h5>
                    <p className="mb-0">
                      You will receive an email confirmation shortly. Once your order is dispatched,
                      we'll send you tracking information so you can follow your package's journey.
                    </p>
                  </div>
                  */}
                </div>
              </div>
            </>
          ) : (
            <div className="row">
              <div className="col-12 text-center py-5">
                <p>No order found</p>
                <Link to="/" className="btn pink-gradient-btn-into-black">
                  Return to Home
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

export default OrderConfirmation;
